import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { normalizeUnionRosterDirectory } from '../domain/unionRoster.js';

const ROSTER_ERROR_MESSAGES = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
  union_not_active: '현재 활성 상태인 유니온이 아닙니다.',
  union_manager_required: '다른 멤버의 접속시간은 주딱 또는 파딱만 변경할 수 있습니다.',
  availability_self_only: '일반 멤버는 자신의 접속시간만 변경할 수 있습니다.',
  roster_member_not_found: '현재 게임 멤버 명단에서 해당 멤버를 찾지 못했습니다.',
  invalid_member_openid: '멤버 식별값을 확인하지 못했습니다.',
  invalid_availability_windows: '접속 가능 시간대를 확인해주세요.',
};

const ROSTER_CACHE_TTL = 15_000;
let rosterCache = null;
let rosterCacheAt = 0;
let rosterRequest = null;

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  return supabase;
}

function migrationMissing(error) {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return code === 'PGRST202' || code === '42883' || /get_current_union_roster_context|union_member_availability_windows|set_current_union_member_availability_windows|list_current_union_roster/i.test(message);
}

function unifiedContextMissing(error) {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return code === 'PGRST202' || code === '42883' || /get_current_union_roster_context/i.test(message);
}

function createError(error, fallback) {
  if (migrationMissing(error)) {
    const next = new Error('멤버 접속시간 DB 마이그레이션 적용이 필요합니다.');
    next.code = 'availability_migration_required';
    return next;
  }
  const raw = String(error?.message || '');
  const code = Object.keys(ROSTER_ERROR_MESSAGES).find((item) => raw.includes(item));
  const next = new Error(ROSTER_ERROR_MESSAGES[code] || raw || fallback || '게임 멤버 명단을 처리하지 못했습니다.');
  next.code = code || error?.code || 'unknown_error';
  return next;
}

function createAvailabilitySaveError(error) {
  const raw = String(error?.message || '');
  const postgresCode = String(error?.code || '');
  if (postgresCode === '42702' || /column reference\s+"?[a-z0-9_]+"?\s+is ambiguous/i.test(raw)) {
    const next = new Error('접속 가능 시간을 저장하지 못했습니다. 화면을 새로고침한 뒤 다시 시도해주세요.');
    next.code = 'availability_save_failed';
    return next;
  }
  return createError(error, '멤버 접속시간을 저장하지 못했습니다.');
}

function normalizeWindows(value) {
  const windows = (Array.isArray(value) ? value : []).map((item) => ({
    start: String(item?.start || item?.start_at || '').slice(0, 5),
    end: String(item?.end || item?.end_at || '').slice(0, 5),
  })).filter((item) => item.start && item.end);
  for (const item of windows) {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(item.start) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(item.end) || item.start === item.end) {
      throw createError({ message: 'invalid_availability_windows' });
    }
  }
  return windows.slice(0, 8);
}

function combineRosterContext(payload, currentUserId = '') {
  const directory = normalizeUnionRosterDirectory(payload || {});
  const availabilityRows = Array.isArray(payload?.availability_windows) ? payload.availability_windows : [];
  const pushRows = Array.isArray(payload?.push_subscriptions) ? payload.push_subscriptions : [];
  const byOpenid = new Map();
  const pushDevicesByUserId = new Map();
  for (const row of availabilityRows) {
    const key = String(row?.member_openid || '');
    if (!key) continue;
    const list = byOpenid.get(key) || [];
    list.push({ start: String(row?.start_at || '').slice(0, 5), end: String(row?.end_at || '').slice(0, 5) });
    byOpenid.set(key, list);
  }
  for (const row of pushRows) {
    const userId = String(row?.user_id || '').trim();
    if (!userId) continue;
    pushDevicesByUserId.set(userId, Math.max(0, Number(row?.active_device_count) || 0));
  }
  const viewerUserId = String(payload?.viewer_user_id || currentUserId || '');
  const members = directory.members.map((member) => {
    const windows = (byOpenid.get(member.memberOpenid) || []).sort((a, b) => a.start.localeCompare(b.start));
    const linkedUserId = String(member.linkedUserId || '').trim();
    const pushDeviceCount = linkedUserId ? pushDevicesByUserId.get(linkedUserId) || 0 : 0;
    return {
      ...member,
      availabilityWindows: windows,
      availableAt: windows[0]?.start || '',
      isSelf: Boolean(viewerUserId && member.linkedUserId === viewerUserId),
      pushEnabled: pushDeviceCount > 0,
      pushDeviceCount,
    };
  });
  return {
    ...directory,
    members,
    pushMemberCount: members.filter((member) => member.pushEnabled).length,
    availabilityMigrationRequired: false,
  };
}

async function loadRosterFallback(client) {
  const [{ data, error }, userResult] = await Promise.all([client.rpc('list_current_union_roster'), client.auth.getUser()]);
  if (error) throw createError(error, '게임 멤버 명단을 불러오지 못했습니다.');
  const directory = normalizeUnionRosterDirectory(data || {});
  if (!directory.unionId || !directory.members.length) return directory;

  const availability = await client.from('union_member_availability_windows')
    .select('member_openid,slot_no,start_at,end_at').eq('union_id', directory.unionId).order('member_openid').order('slot_no');
  if (availability.error) throw createError(availability.error, '멤버 접속시간을 불러오지 못했습니다.');
  return combineRosterContext({ ...data, availability_windows: availability.data || [] }, userResult.data?.user?.id);
}

async function requestCurrentUnionRoster() {
  const client = requireClient();
  const unified = await client.rpc('get_current_union_roster_context');
  if (!unified.error) return combineRosterContext(unified.data || {});
  if (!unifiedContextMissing(unified.error)) throw createError(unified.error, '게임 멤버 명단을 불러오지 못했습니다.');
  return loadRosterFallback(client);
}

export function invalidateCurrentUnionRosterCache() {
  rosterCache = null;
  rosterCacheAt = 0;
}

export async function loadCurrentUnionRoster({ force = false } = {}) {
  const now = Date.now();
  if (!force && rosterCache && now - rosterCacheAt < ROSTER_CACHE_TTL) return rosterCache;
  if (!force && rosterRequest) return rosterRequest;

  rosterRequest = requestCurrentUnionRoster()
    .then((result) => {
      rosterCache = result;
      rosterCacheAt = Date.now();
      return result;
    })
    .finally(() => {
      rosterRequest = null;
    });
  return rosterRequest;
}

export async function saveUnionMemberAvailability({ memberOpenid, windows }) {
  const normalizedOpenid = String(memberOpenid || '').trim();
  if (!normalizedOpenid) throw createError({ message: 'invalid_member_openid' });
  const normalizedWindows = normalizeWindows(windows);
  const client = requireClient();
  const { data, error } = await client.rpc('set_current_union_member_availability_windows', { target_member_openid: normalizedOpenid, target_windows: normalizedWindows });
  if (error) throw createAvailabilitySaveError(error);
  invalidateCurrentUnionRosterCache();
  return { memberOpenid: String(data?.member_openid || normalizedOpenid), availabilityWindows: normalizeWindows(data?.windows || normalizedWindows) };
}
